import { useState, useEffect, useRef } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { AdminGuard } from '@/components/admin/AdminGuard';
import BillingAnalytics from '@/components/admin/BillingAnalytics';
import { BillingInterruptionReport } from '@/components/admin/BillingInterruptionReport';
import CostSavingsTrendChart from '@/components/admin/CostSavingsTrendChart';
import CostSavingsComparison from '@/components/admin/CostSavingsComparison';
import CostSavingsForecast from '@/components/admin/CostSavingsForecast';
import CostSavingsGoals from '@/components/admin/CostSavingsGoals';
import AgencyComparisonTable from '@/components/admin/AgencyComparisonTable';
import CumulativeBillableHoursChart from '@/components/admin/CumulativeBillableHoursChart';
import CareUtilizationMixChart from '@/components/admin/CareUtilizationMixChart';
import { Helmet } from 'react-helmet-async';
import { BarChart3, Building2, CalendarIcon, FileDown, Loader2, Filter, RotateCcw, Activity, Settings2, Mail, Download } from 'lucide-react';
import ChartThemeToggle from '@/components/dashboard/ChartThemeToggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { exportFullAnalyticsToPDF } from '@/utils/exportFullAnalytics';
import { captureCharts, ChartRefs } from '@/utils/captureCharts';
import { toast } from 'sonner';

type RecommendationType = 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW';

const RECOMMENDATION_OPTIONS: { value: RecommendationType; label: string; color: string }[] = [
  { value: 'HOME', label: 'Home Care', color: 'bg-blue-500' },
  { value: 'PCP', label: 'PCP', color: 'bg-purple-500' },
  { value: 'URGENT_CARE', label: 'Urgent Care', color: 'bg-orange-500' },
  { value: 'ER_NOW', label: 'ER', color: 'bg-red-500' },
];

type ChartKey = 'costSavingsAnalytics' | 'costSavingsTrend' | 'costSavingsComparison' | 'costSavingsForecast' | 'costSavingsGoals' | 'agencyComparison' | 'cumulativeBillableHours' | 'careUtilizationMix';

interface ChartOption {
  key: ChartKey;
  label: string;
  description: string;
  icon: string;
}

const CHART_OPTIONS: ChartOption[] = [
  { key: 'costSavingsAnalytics', label: 'Billing Analytics', description: 'Revenue and profit impact from billing hours', icon: '📊' },
  { key: 'costSavingsTrend', label: 'Savings Trend', description: 'Monthly savings over last 12 months', icon: '📈' },
  { key: 'costSavingsComparison', label: 'Period Comparison', description: 'Current vs previous period metrics', icon: '⚖️' },
  { key: 'costSavingsForecast', label: 'Savings Forecast', description: 'Projected future savings', icon: '🔮' },
  { key: 'costSavingsGoals', label: 'Savings Goals', description: 'Goal tracking and progress', icon: '🎯' },
  { key: 'agencyComparison', label: 'Agency Comparison', description: 'Cross-agency performance table', icon: '🏢' },
  { key: 'cumulativeBillableHours', label: 'Cumulative Billable Hours', description: 'Total billable hours saved over time', icon: '⏱️' },
  { key: 'careUtilizationMix', label: 'Care Utilization Mix', description: 'Triage distribution by agency', icon: '🏥' },
];

const EXPORT_PREFS_KEY = 'checkfirst-analytics-export-prefs';
const DEFAULT_CHARTS: ChartKey[] = ['costSavingsAnalytics', 'costSavingsTrend', 'costSavingsComparison', 'costSavingsForecast'];

const loadSavedPreferences = (): ChartKey[] => {
  try {
    const saved = localStorage.getItem(EXPORT_PREFS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as ChartKey[];
      // Validate that all saved values are valid ChartKeys
      const validKeys = CHART_OPTIONS.map(o => o.key);
      return parsed.filter(k => validKeys.includes(k));
    }
  } catch (e) {
    console.error('Error loading export preferences:', e);
  }
  return DEFAULT_CHARTS;
};

const savePreferences = (charts: ChartKey[]): void => {
  try {
    localStorage.setItem(EXPORT_PREFS_KEY, JSON.stringify(charts));
  } catch (e) {
    console.error('Error saving export preferences:', e);
  }
};

interface Agency {
  id: string;
  name: string;
  code: string;
}

export default function AdminAnalyticsPage() {
  const { user } = useAdminAuth();
  const [searchParams] = useSearchParams();
  const [hospitalVisitCost, setHospitalVisitCost] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedCharts, setSelectedCharts] = useState<ChartKey[]>(loadSavedPreferences);
  const [preferencesModified, setPreferencesModified] = useState(false);
  const [pdfOrientation, setPdfOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [deliveryMethod, setDeliveryMethod] = useState<'download' | 'email'>('download');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedRecommendations, setSelectedRecommendations] = useState<RecommendationType[]>([
    'HOME', 'PCP', 'URGENT_CARE', 'ER_NOW'
  ]);
  
  // Supervisor assigned patient IDs
  const [assignedPatientIds, setAssignedPatientIds] = useState<string[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  
  // Create stable refs for chart capture
  const analyticsRef = useRef<HTMLDivElement>(null);
  const trendRef = useRef<HTMLDivElement>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);
  const forecastRef = useRef<HTMLDivElement>(null);
  const goalsRef = useRef<HTMLDivElement>(null);
  const agencyCompRef = useRef<HTMLDivElement>(null);
  const cumulativeBillableHoursRef = useRef<HTMLDivElement>(null);
  const careUtilizationMixRef = useRef<HTMLDivElement>(null);
  
  // Date range state - default to current month
  const [startDate, setStartDate] = useState<Date | undefined>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());

  const toggleRecommendation = (rec: RecommendationType) => {
    setSelectedRecommendations(prev => 
      prev.includes(rec) 
        ? prev.filter(r => r !== rec)
        : [...prev, rec]
    );
  };

  const toggleChart = (chart: ChartKey) => {
    setSelectedCharts(prev => {
      const newSelection = prev.includes(chart) 
        ? prev.filter(c => c !== chart)
        : [...prev, chart];
      setPreferencesModified(true);
      return newSelection;
    });
  };

  const selectAllCharts = () => {
    const availableCharts = getAvailableCharts();
    setSelectedCharts(availableCharts.map(c => c.key));
    setPreferencesModified(true);
  };

  const deselectAllCharts = () => {
    setSelectedCharts([]);
    setPreferencesModified(true);
  };

  const handleSavePreferences = () => {
    savePreferences(selectedCharts);
    setPreferencesModified(false);
    toast.success('Export preferences saved');
  };

  const handleResetPreferences = () => {
    setSelectedCharts(DEFAULT_CHARTS);
    savePreferences(DEFAULT_CHARTS);
    setPreferencesModified(false);
    toast.info('Preferences reset to defaults');
  };

  // Get charts that are currently visible based on context
  const getAvailableCharts = (): ChartOption[] => {
    return CHART_OPTIONS.filter(option => {
      if (option.key === 'agencyComparison') {
        return isAllAgencies && isSuperAdmin;
      }
      if (option.key === 'costSavingsGoals') {
        return effectiveAgencyId && !isAllAgencies && !isSupervisor;
      }
      if (option.key === 'cumulativeBillableHours') {
        return !isSupervisor && (effectiveAgencyId || isAllAgencies);
      }
      if (option.key === 'careUtilizationMix') {
        return isAllAgencies && isSuperAdmin;
      }
      return effectiveAgencyId || isAllAgencies;
    });
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const isAgencyAdmin = user?.role === 'agency_admin';
  const isSupervisor = user?.role === 'supervisor';
  
  // Check for impersonation mode
  const isImpersonating = searchParams.get('impersonate') === 'agency';
  const impersonatedAgencyId = searchParams.get('aid');
  
  // Load assigned patient IDs for supervisors
  useEffect(() => {
    const loadAssignedPatients = async () => {
      if (!isSupervisor || !user?.id) return;
      
      setLoadingAssignments(true);
      try {
        const { data, error } = await supabase
          .from('patient_assignments')
          .select('patient_id')
          .eq('staff_user_id', user.id)
          .is('revoked_at', null);

        if (error) throw error;
        setAssignedPatientIds((data || []).map(a => a.patient_id));
      } catch (error) {
        console.error('Error loading assigned patients:', error);
      } finally {
        setLoadingAssignments(false);
      }
    };

    loadAssignedPatients();
  }, [isSupervisor, user?.id]);
  
  // Determine effective agency ID based on role and selection
  // "all" means aggregate across all agencies (null for DB function)
  // HIPAA COMPLIANCE: Agency admins can ONLY see their own agency data
  const getEffectiveAgencyId = (): string | null => {
    // If impersonating an agency, use that agency's ID
    if (isImpersonating && impersonatedAgencyId) {
      return impersonatedAgencyId;
    }
    
    // Super admin can view all agencies or filter by specific agency
    if (isSuperAdmin) {
      // "all" selection means null (aggregate all) - only super admins can do this
      return selectedAgencyId === 'all' ? null : selectedAgencyId;
    }
    
    // STRICT HIPAA COMPLIANCE: Agency admins and supervisors MUST only see their own agency
    // This is enforced both here (UI) and in the database (RLS)
    return user?.agency_id || null;
  };

  const effectiveAgencyId = getEffectiveAgencyId();
  const isAllAgencies = isSuperAdmin && selectedAgencyId === 'all';

  // Load agencies for super admin filter
  useEffect(() => {
    const loadAgencies = async () => {
      if (!isSuperAdmin) return;
      
      setLoadingAgencies(true);
      try {
        const { data, error } = await supabase
          .from('agencies')
          .select('id, name, code')
          .eq('is_approved', true)
          .order('name', { ascending: true });

        if (error) throw error;
        
        setAgencies(data || []);
        
        // Auto-select "All Agencies" by default if none selected
        if (!selectedAgencyId && !impersonatedAgencyId) {
          setSelectedAgencyId('all');
        }
      } catch (error) {
        console.error('Error loading agencies:', error);
      } finally {
        setLoadingAgencies(false);
      }
    };

    loadAgencies();
  }, [isSuperAdmin]);

  useEffect(() => {
    const loadAgencySettings = async () => {
      // For "all agencies" mode, we use default costs
      if (isAllAgencies || !effectiveAgencyId) {
        setHospitalVisitCost(null); // Use default costs
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('agencies')
          .select('hospital_visit_cost')
          .eq('id', effectiveAgencyId)
          .maybeSingle();

        if (error) throw error;
        setHospitalVisitCost(data?.hospital_visit_cost as number | null);
      } catch (error) {
        console.error('Error loading agency settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAgencySettings();
  }, [effectiveAgencyId, isAllAgencies]);

  // Get selected agency name for display
  const selectedAgency = agencies.find(a => a.id === selectedAgencyId);

  // Open export dialog
  const openExportDialog = () => {
    // Pre-select available charts
    const available = getAvailableCharts().map(c => c.key);
    setSelectedCharts(prev => prev.filter(c => available.includes(c)));
    setExportDialogOpen(true);
  };

  // Export full analytics to PDF with chart screenshots
  const handleExportFullAnalytics = async () => {
    if (selectedCharts.length === 0) {
      toast.error('Please select at least one chart to export');
      return;
    }

    if (deliveryMethod === 'email' && !recipientEmail) {
      toast.error('Please enter an email address');
      return;
    }

    // Validate email format
    if (deliveryMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(recipientEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    setExportDialogOpen(false);
    setExporting(true);
    try {
      // Only capture selected charts
      const refs: ChartRefs = {
        costSavingsAnalytics: selectedCharts.includes('costSavingsAnalytics') ? analyticsRef.current : null,
        costSavingsTrend: selectedCharts.includes('costSavingsTrend') ? trendRef.current : null,
        costSavingsComparison: selectedCharts.includes('costSavingsComparison') ? comparisonRef.current : null,
        costSavingsForecast: selectedCharts.includes('costSavingsForecast') ? forecastRef.current : null,
        costSavingsGoals: selectedCharts.includes('costSavingsGoals') ? goalsRef.current : null,
        agencyComparison: selectedCharts.includes('agencyComparison') ? agencyCompRef.current : null,
        cumulativeBillableHours: selectedCharts.includes('cumulativeBillableHours') ? cumulativeBillableHoursRef.current : null,
        careUtilizationMix: selectedCharts.includes('careUtilizationMix') ? careUtilizationMixRef.current : null,
      };

      toast.info('Capturing charts...', { duration: 2000 });
      
      const chartCaptures = await captureCharts(refs);
      
      const agencyDisplayName = isAllAgencies ? 'All Agencies' : (selectedAgency?.name || user?.agency?.name);
      const dateRangeText = startDate && endDate 
        ? `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
        : 'All Time';

      const result = await exportFullAnalyticsToPDF({
        agencyId: effectiveAgencyId,
        agencyName: agencyDisplayName,
        customHospitalCost: hospitalVisitCost,
        startDate,
        endDate,
        isAllAgencies,
        chartCaptures,
        orientation: pdfOrientation,
        deliveryMethod,
        recipientEmail,
        recipientName: user?.email
      });

      if (deliveryMethod === 'email' && result.pdfBase64) {
        toast.info('Sending email...', { duration: 2000 });
        
        const { error } = await supabase.functions.invoke('send-analytics-report', {
          body: {
            recipientEmail,
            recipientName: user?.email,
            pdfBase64: result.pdfBase64,
            reportTitle: 'Analytics Report',
            agencyName: agencyDisplayName,
            dateRange: dateRangeText
          }
        });

        if (error) {
          throw new Error(error.message || 'Failed to send email');
        }

        toast.success(`Report sent to ${recipientEmail}`);
        setRecipientEmail('');
      } else if (result.success) {
        toast.success('Full analytics report downloaded successfully');
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting analytics:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to export analytics report');
    } finally {
      setExporting(false);
    }
  };

  if ((loading && !isSuperAdmin) || loadingAssignments) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="space-y-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="h-64 bg-muted rounded-lg"></div>
              <div className="h-64 bg-muted rounded-lg"></div>
            </div>
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  // Determine if we should show analytics
  const canShowAnalytics = effectiveAgencyId || isAllAgencies || (isSupervisor && assignedPatientIds.length >= 0);
  // For supervisors, pass patientIds to filter data
  const supervisorPatientIds = isSupervisor ? assignedPatientIds : undefined;

  return (
    <AdminGuard>
      <Helmet>
        <title>Analytics | CheckFirst Admin</title>
        <meta name="description" content="View cost savings analytics and performance metrics for your healthcare triage system." />
      </Helmet>
      <AdminLayout>
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
                <p className="text-muted-foreground">
                  {isSuperAdmin 
                    ? 'Cost savings and performance metrics across all agencies'
                    : isSupervisor
                      ? `Cost savings for your ${assignedPatientIds.length} assigned patient${assignedPatientIds.length !== 1 ? 's' : ''}`
                      : `Cost savings and performance metrics for ${user?.agency?.name || 'your agency'}`
                  }
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <ChartThemeToggle />
              <Button
                onClick={openExportDialog}
                disabled={exporting || (!effectiveAgencyId && !isAllAgencies)}
                className="gap-2"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                Export Full Report
              </Button>
            </div>
          </div>

          {/* Export Dialog */}
          <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" />
                  Export Options
                </DialogTitle>
                <DialogDescription>
                  Select which charts to include in your PDF report
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Delivery Method Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Delivery Method</span>
                  </div>
                  <div className="flex rounded-lg border border-border overflow-hidden">
                    <button
                      onClick={() => setDeliveryMethod('download')}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                        deliveryMethod === 'download'
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </button>
                    <button
                      onClick={() => setDeliveryMethod('email')}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                        deliveryMethod === 'email'
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Email
                    </button>
                  </div>
                </div>

                {/* Email Input */}
                {deliveryMethod === 'email' && (
                  <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/5">
                    <Label htmlFor="recipient-email" className="text-sm font-medium">
                      Recipient Email
                    </Label>
                    <Input
                      id="recipient-email"
                      type="email"
                      placeholder="Enter email address"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="h-9"
                    />
                    <p className="text-xs text-muted-foreground">
                      The PDF report will be sent as an attachment to this email address.
                    </p>
                  </div>
                )}

                {/* Orientation Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Page Orientation</span>
                  </div>
                  <div className="flex rounded-lg border border-border overflow-hidden">
                    <button
                      onClick={() => setPdfOrientation('portrait')}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                        pdfOrientation === 'portrait'
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <span className="text-base">↕</span>
                      Portrait
                    </button>
                    <button
                      onClick={() => setPdfOrientation('landscape')}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                        pdfOrientation === 'landscape'
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted"
                      )}
                    >
                      <span className="text-base">↔</span>
                      Landscape
                    </button>
                  </div>
                </div>

                {pdfOrientation === 'landscape' && (
                  <p className="text-xs text-muted-foreground px-1">
                    Landscape mode provides more horizontal space for charts with wide data ranges.
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    {selectedCharts.length} of {getAvailableCharts().length} selected
                  </span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllCharts} className="text-xs h-7">
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={deselectAllCharts} className="text-xs h-7">
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {getAvailableCharts().map((option) => (
                    <div
                      key={option.key}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedCharts.includes(option.key)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      )}
                      onClick={() => toggleChart(option.key)}
                    >
                      <Checkbox
                        checked={selectedCharts.includes(option.key)}
                        onCheckedChange={() => toggleChart(option.key)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{option.icon}</span>
                          <span className="font-medium text-sm text-foreground">{option.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Save preferences section */}
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    {preferencesModified && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        • Unsaved changes
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleResetPreferences}
                      className="text-xs h-7"
                    >
                      Reset to Defaults
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      onClick={handleSavePreferences}
                      disabled={!preferencesModified}
                      className="text-xs h-7"
                    >
                      Save Preferences
                    </Button>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleExportFullAnalytics} 
                  disabled={selectedCharts.length === 0 || (deliveryMethod === 'email' && !recipientEmail)}
                  className="gap-2"
                >
                  {deliveryMethod === 'email' ? (
                    <Mail className="h-4 w-4" />
                  ) : (
                    <FileDown className="h-4 w-4" />
                  )}
                  {deliveryMethod === 'email' ? 'Send Email' : 'Download PDF'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Filters - Date range applies to Summary & Agency Comparison only */}
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-muted/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">Filters</span>
                <span className="text-xs text-muted-foreground">(Date range applies to Summary &amp; Agency Comparison)</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-xs h-7"
                  onClick={() => {
                    setStartDate(startOfMonth(new Date()));
                    setEndDate(new Date());
                    if (isSuperAdmin) setSelectedAgencyId('all');
                    setSelectedRecommendations(['HOME', 'PCP', 'URGENT_CARE', 'ER_NOW']);
                  }}
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Reset
                </Button>
              </div>
              
              <div className="flex flex-wrap items-end gap-6">
                {/* Agency Filter for Super Admin */}
                {isSuperAdmin && !isImpersonating && (
                  <div className="space-y-1.5">
                    <Label htmlFor="agency-filter" className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5" />
                      Agency
                    </Label>
                    <Select
                      value={selectedAgencyId || 'all'}
                      onValueChange={(value) => setSelectedAgencyId(value)}
                      disabled={loadingAgencies}
                    >
                      <SelectTrigger id="agency-filter" className="w-[220px] h-9">
                        <SelectValue placeholder={loadingAgencies ? "Loading..." : "Select an agency"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="font-medium">
                          📊 All Agencies (Aggregated)
                        </SelectItem>
                        <div className="my-1 border-t border-border" />
                        {agencies.map((agency) => (
                          <SelectItem key={agency.id} value={agency.id}>
                            {agency.name} ({agency.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Date Range Filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Date Range
                  </Label>
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "justify-start text-left font-normal w-[130px] h-9",
                            !startDate && "text-muted-foreground"
                          )}
                        >
                          {startDate ? format(startDate, "MMM d, yyyy") : "Start date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>

                    <span className="text-muted-foreground text-sm">to</span>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "justify-start text-left font-normal w-[130px] h-9",
                            !endDate && "text-muted-foreground"
                          )}
                        >
                          {endDate ? format(endDate, "MMM d, yyyy") : "End date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Quick presets */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Quick Select</Label>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs h-9 px-3"
                      onClick={() => {
                        setStartDate(startOfMonth(new Date()));
                        setEndDate(new Date());
                      }}
                    >
                      This Month
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs h-9 px-3"
                      onClick={() => {
                        const lastMonth = subMonths(new Date(), 1);
                        setStartDate(startOfMonth(lastMonth));
                        setEndDate(endOfMonth(lastMonth));
                      }}
                    >
                      Last Month
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs h-9 px-3"
                      onClick={() => {
                        setStartDate(subDays(new Date(), 90));
                        setEndDate(new Date());
                      }}
                    >
                      Last 90 Days
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs h-9 px-3"
                      onClick={() => {
                        setStartDate(startOfYear(new Date()));
                        setEndDate(new Date());
                      }}
                    >
                      YTD
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs h-9 px-3"
                      onClick={() => {
                        setStartDate(undefined);
                        setEndDate(undefined);
                      }}
                    >
                      All Time
                    </Button>
                  </div>
                </div>

                {/* Recommendation Type Filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5" />
                    Care Levels
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    {RECOMMENDATION_OPTIONS.map((option) => (
                      <div key={option.value} className="flex items-center gap-1.5">
                        <Checkbox
                          id={`rec-${option.value}`}
                          checked={selectedRecommendations.includes(option.value)}
                          onCheckedChange={() => toggleRecommendation(option.value)}
                          className="h-4 w-4"
                        />
                        <label
                          htmlFor={`rec-${option.value}`}
                          className="flex items-center gap-1 text-xs cursor-pointer select-none"
                        >
                          <span className={cn("w-2 h-2 rounded-full", option.color)} />
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Show message if no agency selected (only for non-super admin without agency) */}
          {!effectiveAgencyId && !isAllAgencies && !isSuperAdmin && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Agency Selected</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Please select an agency from the dropdown above to view analytics data.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Agency Comparison Table - SUPER ADMIN ONLY */}
          {/* HIPAA Compliance: Only super admins can view cross-agency comparison data */}
          {isAllAgencies && isSuperAdmin && (
            <div ref={agencyCompRef}>
              <AgencyComparisonTable 
                startDate={startDate} 
                endDate={endDate} 
                selectedRecommendations={selectedRecommendations}
              />
            </div>
          )}

          {/* Billing Analytics - New billing-based calculations */}
          {canShowAnalytics && (
            <div ref={analyticsRef}>
              <BillingAnalytics 
                agencyId={effectiveAgencyId} 
                startDate={startDate}
                endDate={endDate}
                patientIds={supervisorPatientIds}
              />
            </div>
          )}

          {/* Cost Savings Trend Chart */}
          {canShowAnalytics && (
            <div ref={trendRef}>
              <CostSavingsTrendChart
                agencyId={effectiveAgencyId}
                customHospitalCost={hospitalVisitCost}
                selectedRecommendations={selectedRecommendations}
                patientIds={supervisorPatientIds}
              />
            </div>
          )}

          {/* Cost Savings Comparison */}
          {canShowAnalytics && (
            <div ref={comparisonRef}>
              <CostSavingsComparison
                agencyId={effectiveAgencyId}
                customHospitalCost={hospitalVisitCost}
                selectedRecommendations={selectedRecommendations}
                patientIds={supervisorPatientIds}
              />
            </div>
          )}
          {canShowAnalytics && (
            <div ref={forecastRef}>
              <CostSavingsForecast
                agencyId={effectiveAgencyId}
                customHospitalCost={hospitalVisitCost}
                selectedRecommendations={selectedRecommendations}
                patientIds={supervisorPatientIds}
              />
            </div>
          )}

          {/* Cumulative Billable Hours Chart - Shows for all views */}
          {!isSupervisor && (
            <div ref={cumulativeBillableHoursRef}>
              <CumulativeBillableHoursChart 
                agencyId={isAllAgencies ? null : effectiveAgencyId} 
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          )}

          {/* Care Utilization Mix Chart - Only for All Agencies view */}
          {isAllAgencies && isSuperAdmin && (
            <div ref={careUtilizationMixRef}>
              <CareUtilizationMixChart 
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          )}

          {/* Billing Interruption Report - Shows hospitalization impact */}
          {effectiveAgencyId && !isAllAgencies && !isSupervisor && (
            <BillingInterruptionReport agencyId={effectiveAgencyId} />
          )}

          {/* Cost Savings Goals - Only show for specific agency, not aggregated or supervisors */}
          {effectiveAgencyId && !isAllAgencies && !isSupervisor && (
            <div ref={goalsRef}>
              <CostSavingsGoals agencyId={effectiveAgencyId} />
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}
